import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for auto-refreshing data at specified intervals
 * @param {Function} fetchFunction - The function to call to fetch data
 * @param {number} intervalMs - Refresh interval in milliseconds (default: 30 seconds)
 * @param {boolean} enabled - Whether auto-refresh is enabled (default: true)
 * @returns {Object} - { data, loading, error, lastUpdated, refresh, isAutoRefreshEnabled, toggleAutoRefresh }
 */
export const useAutoRefresh = (fetchFunction, intervalMs = 30000, enabled = true) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(enabled);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    const fetchData = useCallback(async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const result = await fetchFunction();
            if (isMountedRef.current) {
                setData(result);
                setLastUpdated(new Date());
                setError(null);
            }
        } catch (err) {
            if (isMountedRef.current) {
                setError(err.message || 'Failed to fetch data');
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [fetchFunction]);

    // Manual refresh function
    const refresh = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    // Toggle auto-refresh
    const toggleAutoRefresh = useCallback(() => {
        setIsAutoRefreshEnabled(prev => !prev);
    }, []);

    // Initial fetch
    useEffect(() => {
        isMountedRef.current = true;
        fetchData(true);

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchData]);

    // Set up auto-refresh interval
    useEffect(() => {
        if (isAutoRefreshEnabled && intervalMs > 0) {
            intervalRef.current = setInterval(() => {
                fetchData(false); // Silent refresh (no loading state)
            }, intervalMs);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isAutoRefreshEnabled, intervalMs, fetchData]);

    return {
        data,
        loading,
        error,
        lastUpdated,
        refresh,
        isAutoRefreshEnabled,
        toggleAutoRefresh
    };
};

/**
 * Format time ago string
 * @param {Date} date 
 * @returns {string}
 */
export const formatTimeAgo = (date) => {
    if (!date) return 'Never';

    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return date.toLocaleTimeString();
};

export default useAutoRefresh;
